import { useContext, useState } from 'react';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { DemoBanner } from '@/components/common/DemoBanner';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '@lib/context/AuthContextType';
import { isEmbeddedPreview } from '@/lib/utils/tokenStorage';
import { FloatingRequestButton } from '@/components/dev/FloatingRequestButton';

interface NavItem { to: string; icon: string; label: string; }

// Four core tabs + "More" (popup menu) — seven inline tabs was too cramped.
const employeeNav: NavItem[] = [
  { to: '/portal/employee', icon: 'home', label: 'Home' },
  { to: '/portal/employee/timesheet', icon: 'schedule', label: 'Hours & Pay' },
  { to: '/portal/employee/documents', icon: 'folder', label: 'Docs' },
  { to: '/portal/employee/calculator', icon: 'calculate', label: 'Calc' },
];

// Everything reachable from the More sheet — BC Code gets top billing.
const employeeMore: (NavItem & { desc?: string; accent?: boolean })[] = [
  { to: '/portal/employee/bc-code', icon: 'menu_book', label: 'BC Code 2024', desc: 'Search the building code', accent: true },
  { to: '/portal/employee/safety', icon: 'health_and_safety', label: 'Safety', desc: 'WorkSafeBC rules' },
  { to: '/portal/employee/jobs', icon: 'construction', label: 'My Jobs', desc: 'Tasks & site details' },
  { to: '/portal/employee/paystubs', icon: 'payments', label: 'Paystubs', desc: 'Pay records' },
  { to: '/portal/employee/help', icon: 'help', label: 'Help', desc: 'How the portal works' },
  { to: '/portal/employee/profile', icon: 'manage_accounts', label: 'Account', desc: 'Profile & password' },
];

// CR #156 — six tabs at 390px works because labels stay short; each tab is
// ~65px wide × 48px tall (≥44px touch target). "Projects" sits right after
// Schedule so the two ways of following the work are side by side.
const clientNav: NavItem[] = [
  { to: '/portal/client', icon: 'home', label: 'Home' },
  { to: '/portal/client/schedule', icon: 'calendar_month', label: 'Schedule' },
  { to: '/portal/client/jobs', icon: 'construction', label: 'Projects' },
  { to: '/portal/client/invoices', icon: 'receipt_long', label: 'Invoices' },
  { to: '/portal/client/documents', icon: 'folder', label: 'Docs' },
  { to: '/portal/client/profile', icon: 'manage_accounts', label: 'Account' },
];

interface PortalLayoutProps {
  children: React.ReactNode;
}

export const PortalLayout = ({ children }: PortalLayoutProps) => {
  const { user, logout } = useContext(AuthContext)!;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const isEmployee = user?.role === 'employee';
  const nav = isEmployee ? employeeNav : clientNav;

  const handleLogout = () => {
    logout();
    navigate('/portal/login');
  };

  // Current page lives in the More sheet → keep the More tab highlighted
  const onMorePage = isEmployee && employeeMore.some(m => pathname.startsWith(m.to));

  return (
    <div className="min-h-screen bg-[#FAF6F0] flex flex-col">
      <DemoBanner />
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2">
          <span className="text-[#C77D3F] font-bold text-lg tracking-tight">OchrePoint</span>
          <span className="text-gray-400 text-sm hidden sm:inline">
            {isEmployee ? 'Employee Portal' : 'Client Portal'}
          </span>
        </div>
        <div className="flex items-center gap-1">
        <ThemeToggle />
        <button onClick={handleLogout} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1">
          <span className="material-symbols-outlined text-base">logout</span>
          <span className="hidden sm:inline">Sign out</span>
        </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-4 py-5 pb-24 max-w-2xl mx-auto w-full">
        {children}
      </main>

      {/* Change-request button — ONLY inside an admin's portal-preview window,
          never for a real client/employee (isEmbeddedPreview is true only in
          the preview iframe's realm). Lets you flag portal UI from the preview
          exactly like every admin page. */}
      {isEmbeddedPreview() && <FloatingRequestButton />}

      {/* Floating help button (client) — always one tap from a real human.
          Hidden on the Help page itself and behind the lightbox/sheets. */}
      {!isEmployee && !pathname.startsWith('/portal/client/help') && (
        <button
          onClick={() => setHelpOpen(true)}
          aria-label="Need a hand? Get help"
          className="fixed right-4 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-30 flex items-center gap-2 pl-3.5 pr-4 py-3 rounded-full bg-[#2C1810] text-white shadow-lg shadow-black/25 active:scale-95 transition-transform"
        >
          <span className="material-symbols-outlined text-xl">support_agent</span>
          <span className="text-sm font-bold">Need a hand?</span>
        </button>
      )}

      {/* Help sheet (client) — big obvious choices, no jargon */}
      {!isEmployee && helpOpen && (
        <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setHelpOpen(false)}>
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-5 pb-[calc(2rem+env(safe-area-inset-bottom))] shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-10 h-1 rounded-full bg-gray-300 mx-auto mb-4" />
            <p className="text-lg font-black text-[#2C1810]">How can we help?</p>
            <p className="text-sm text-gray-500 mb-4">We're a small local crew — you'll reach a real person.</p>
            <div className="space-y-2.5">
              <a href="tel:+12505100677"
                className="flex items-center gap-3 rounded-2xl border border-gray-200 p-4 active:bg-[#FAF6F0]">
                <span className="material-symbols-outlined text-2xl text-[#C77D3F]">call</span>
                <span>
                  <span className="block text-base font-bold text-gray-900">Call us</span>
                  <span className="block text-sm text-gray-500">(250) 510-0677</span>
                </span>
              </a>
              <a href="mailto:info@ochrepoint.ca"
                className="flex items-center gap-3 rounded-2xl border border-gray-200 p-4 active:bg-[#FAF6F0]">
                <span className="material-symbols-outlined text-2xl text-[#C77D3F]">mail</span>
                <span>
                  <span className="block text-base font-bold text-gray-900">Email us</span>
                  <span className="block text-sm text-gray-500">info@ochrepoint.ca</span>
                </span>
              </a>
              <Link to="/portal/client/help" onClick={() => setHelpOpen(false)}
                className="flex items-center gap-3 rounded-2xl border border-gray-200 p-4 active:bg-[#FAF6F0]">
                <span className="material-symbols-outlined text-2xl text-[#C77D3F]">help</span>
                <span>
                  <span className="block text-base font-bold text-gray-900">Common questions</span>
                  <span className="block text-sm text-gray-500">Paying invoices, documents, the schedule</span>
                </span>
              </Link>
            </div>
            <button onClick={() => setHelpOpen(false)}
              className="mt-4 w-full py-3 rounded-2xl border border-gray-300 text-base font-semibold text-gray-600">
              Close
            </button>
          </div>
        </div>
      )}

      {/* More sheet (employee) */}
      {isEmployee && moreOpen && (
        <div className="fixed inset-0 z-20 bg-black/40" onClick={() => setMoreOpen(false)}>
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-4 pb-[calc(4.5rem+env(safe-area-inset-bottom))] shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-10 h-1 rounded-full bg-gray-300 mx-auto mb-3" />
            <div className="grid grid-cols-2 gap-2">
              {employeeMore.map(item => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMoreOpen(false)}
                  className={`flex items-center gap-3 rounded-xl border p-3 active:scale-[0.98] transition-transform ${
                    item.accent
                      ? 'border-[#C77D3F]/40 bg-gradient-to-br from-[#C77D3F]/15 to-[#C77D3F]/5'
                      : pathname.startsWith(item.to)
                        ? 'border-[#C77D3F]/40 bg-[#C77D3F]/5'
                        : 'border-gray-200 bg-white'
                  }`}
                >
                  <span className={`material-symbols-outlined text-2xl ${item.accent || pathname.startsWith(item.to) ? 'text-[#C77D3F]' : 'text-gray-500'}`}>
                    {item.icon}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-900 truncate">{item.label}</span>
                    {item.desc && <span className="block text-[10px] text-gray-400 truncate">{item.desc}</span>}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around z-30 pb-[env(safe-area-inset-bottom)]">
        {nav.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to.endsWith('/employee') || item.to.endsWith('/client')}
            onClick={() => setMoreOpen(false)}
            className={({ isActive }) =>
              `flex flex-col items-center py-2 px-2 text-[10px] gap-0.5 min-w-0 flex-1 ${isActive && !moreOpen ? 'text-[#C77D3F]' : 'text-gray-500'}`
            }
          >
            <span className="material-symbols-outlined text-xl">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
        {isEmployee && (
          <button
            onClick={() => setMoreOpen(v => !v)}
            className={`flex flex-col items-center py-2 px-2 text-[10px] gap-0.5 min-w-0 flex-1 ${moreOpen || onMorePage ? 'text-[#C77D3F]' : 'text-gray-500'}`}
          >
            <span className="material-symbols-outlined text-xl">{moreOpen ? 'close' : 'apps'}</span>
            More
          </button>
        )}
      </nav>
    </div>
  );
};
